/**
 * Client for the CMS public blog API.
 *
 * Blogs are written and approved in the CMS, then read from here — the site
 * itself no longer holds any post content. Only approved posts targeting this
 * site are returned.
 *
 * Configure in .env:
 *   REACT_APP_CMS_API_URL=http://localhost:4000/api
 *   REACT_APP_CMS_SITE_KEY=jjfindia
 */

const CMS_API_URL = process.env.REACT_APP_CMS_API_URL || 'http://localhost:4000/api';
const CMS_SITE_KEY = process.env.REACT_APP_CMS_SITE_KEY || 'jjfindia';

async function request(path, { signal } = {}) {
  const response = await fetch(`${CMS_API_URL}${path}`, { signal });

  if (!response.ok) {
    const error = new Error(`CMS request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Every approved blog for this site, newest first.
 * @returns {Promise<Array>} posts
 */
export async function fetchBlogs({ limit = 50, page = 1, signal } = {}) {
  const params = new URLSearchParams({
    site: CMS_SITE_KEY,
    limit: String(limit),
    page: String(page)
  });

  const data = await request(`/public/blogs?${params}`, { signal });
  return data.blogs || [];
}

/**
 * A single approved blog by its slug.
 * @returns {Promise<Object|null>} the post, or null when it does not exist
 */
export async function fetchBlogBySlug(slug, { signal } = {}) {
  try {
    return await request(`/public/blogs/${encodeURIComponent(slug)}`, { signal });
  } catch (error) {
    if (error.status === 404) return null;
    throw error;
  }
}

/**
 * Every approved magazine / newsletter issue.
 * @param {string} [year] restrict to one year, e.g. "2026"
 * @returns {Promise<Array>} issues — { id, title, pdfUrl, coverImage, year, createdAt }
 */
export async function fetchMagazines({ year, signal } = {}) {
  const params = new URLSearchParams();
  if (year) params.set('year', String(year));

  const query = params.toString();
  const data = await request(`/public/magazines${query ? `?${query}` : ''}`, { signal });
  return data.magazines || [];
}

/** Format a CMS timestamp the way the blog pages have always shown it: "10 November, 2023" */
export function formatBlogDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getDate()} ${date.toLocaleString('en-GB', { month: 'long' })}, ${date.getFullYear()}`;
}

export { CMS_SITE_KEY };
